import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import styled from 'styled-components';

import { SnackbarService } from '@services';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// Components
import Snackbar from './snackbar';

const MAX_VISIBLE_SNACKS = 5;
const SNACK_ENTER_ANIMATION_MS = 420;
const SNACK_EXIT_ANIMATION_MS = 360;
const SNACK_COLLAPSE_MS = 180;

const initialState = {
  snacks: [],
  queue: [],
};

const generateId = () => {
  const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const ID_LENGTH = 8;

  let randomId = '';
  for (let i = 0; i < ID_LENGTH; i++) {
    randomId += ALPHABET.charAt(Math.floor(Math.random() * ALPHABET.length));
  }
  return randomId;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD_SNACK': {
      const snack = action.payload;

      if (state.snacks.length === MAX_VISIBLE_SNACKS) {
        return {
          ...state,
          queue: [...state.queue, snack],
        };
      }

      return {
        ...state,
        snacks: [...state.snacks, snack],
      };
    }
    case 'REMOVE_SNACK': {
      const filteredSnacks = state.snacks.filter((snack) => snack.id !== action.payload);
      if (filteredSnacks.length === state.snacks.length) {
        return state;
      }

      if (state.queue.length > 0) {
        const [nextSnack, ...restQueue] = state.queue;
        return {
          snacks: [...filteredSnacks, nextSnack],
          queue: restQueue,
        };
      }

      return {
        ...state,
        snacks: filteredSnacks,
      };
    }
    default:
      return state;
  }
};

const SnackbarWrapper = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addSnackbar = useCallback((message = 'Default Message', type = 'default') => {
    dispatch({
      type: 'ADD_SNACK',
      payload: {
        id: generateId(),
        variant: type,
        message,
      },
    });
  }, []);

  const removeSnack = useCallback((id) => {
    dispatch({ type: 'REMOVE_SNACK', payload: id });
  }, []);

  useEffect(() => {
    const subscription = SnackbarService.getSnackbar()
      .subscribe(({ type, message }) => {
        addSnackbar(message, type);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [addSnackbar]);

  const renderedSnacks = useMemo(() => state.snacks.map((snack, index) => (
    <CSSTransition
      key={snack.id}
      classNames="snack-transition"
      timeout={{ enter: SNACK_ENTER_ANIMATION_MS, exit: SNACK_EXIT_ANIMATION_MS + SNACK_COLLAPSE_MS }}
    >
      <Styled.SnackShell>
        <Styled.SnackItem className="snack-item-motion">
          <Snackbar
            id={snack.id}
            variant={snack.variant}
            message={snack.message}
            autoClose={index === 0}
            removeSnack={removeSnack}
          />
        </Styled.SnackItem>
      </Styled.SnackShell>
    </CSSTransition>
  )), [removeSnack, state.snacks]);

  return (
    <Styled.SnackbarWrapper>
      <TransitionGroup component={Styled.SnacksList}>
        {renderedSnacks}
      </TransitionGroup>
    </Styled.SnackbarWrapper>
  );
};

// *************************************
// STYLED COMPONENTS
// *************************************

const Styled = {};

Styled.SnackbarWrapper = styled.div`
  top: 24px;
  right: 0px;
  left: 0px;
  position: fixed;
  z-index: 9999999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  pointer-events: none;
`;

Styled.SnacksList = styled.div`
  width: 100%;
  overflow-x: hidden;

  .snack-transition-enter {
    max-height: 120px;
  }

  .snack-transition-enter-active {
    max-height: 120px;
  }

  .snack-transition-exit {
    max-height: 120px;
  }

  .snack-transition-exit-active {
    max-height: 0;
    transition: max-height ${SNACK_COLLAPSE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${SNACK_EXIT_ANIMATION_MS}ms;
  }

  .snack-transition-enter .snack-item-motion {
    transform: translateX(120%);
  }

  .snack-transition-enter-active .snack-item-motion {
    transform: translateX(0);
    transition: transform ${SNACK_ENTER_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .snack-transition-exit .snack-item-motion {
    transform: translateX(0);
  }

  .snack-transition-exit-active .snack-item-motion {
    transform: translateX(120%);
    transition: transform ${SNACK_EXIT_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

Styled.SnackShell = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  overflow-y: hidden;
  overflow-x: hidden;
  pointer-events: auto;
`;

Styled.SnackItem = styled.div`
  width: 360px;
  max-width: calc(100vw - 48px);
  margin-right: 24px;
  will-change: transform;
  backface-visibility: hidden;
`;

export default SnackbarWrapper;
